import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer, FloppyDisk, PaperPlaneTilt, PencilSimpleLine, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import Wordmark from '../components/Wordmark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultSections = () => ({
  intro: 'Hola, te comparto tu evolución de los últimos 15 días. Sigue así y recuerda: la constancia es el camino más corto hacia la fluidez.',
  strengths: '',
  to_improve: '',
  advice: '',
  closing: 'Un abrazo y a por los próximos 15 días. — Alberto',
});

export default function TeacherReportPage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [editable, setEditable] = useState(defaultSections());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSavedId, setLastSavedId] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/teacher/users/${user_id}/reports`, { withCredentials: true });
      setHistory(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/teacher/users/${user_id}/report-data`, { withCredentials: true });
        setData(res.data);
        // Pre-populate strengths/improvements from actual metrics
        const s = res.data.skills || [];
        const strong = s.filter(k => k.accuracy >= 75).map(k => k.skill).slice(0, 3);
        const weak = s.filter(k => k.accuracy < 60 && k.total >= 5).map(k => k.skill).slice(0, 3);
        setEditable(prev => ({
          ...prev,
          strengths: strong.length
            ? `Destaco tu rendimiento en ${strong.join(', ')}: estás automatizando muy bien estas áreas.`
            : prev.strengths,
          to_improve: weak.length
            ? `Áreas por reforzar: ${weak.join(', ')}. Te dejo tres ejercicios para esta quincena.`
            : prev.to_improve,
        }));
      } catch (e) {
        toast.error('No se pudo cargar el informe');
      }
      finally { setLoading(false); }
    })();
    loadHistory();
  }, [user_id]);

  const updateField = (k, v) => setEditable(p => ({ ...p, [k]: v }));

  const handlePrint = () => window.print();

  const handleSave = async (alsoSend = false) => {
    if (alsoSend) setSending(true); else setSaving(true);
    try {
      const payload = {
        sections: editable,
        report_date: reportDate,
        metrics_snapshot: data,
        send_email: alsoSend,
      };
      const res = await axios.post(
        `${API}/teacher/users/${user_id}/reports`,
        payload,
        { withCredentials: true }
      );
      setLastSavedId(res.data?.report?.report_id);
      if (alsoSend) {
        toast.success(`Informe enviado a ${data.student.email}`);
      } else {
        toast.success('Informe guardado');
      }
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-[#78716C]">Cargando informe…</div>;
  }
  if (!data) {
    return <div className="p-6 text-center text-red-600">Sin datos del alumno</div>;
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="teacher-report-page">
      {/* Controls (non-printable) */}
      <div className="flex flex-wrap items-center gap-3 mb-6 print:hidden">
        <button onClick={() => navigate(-1)} className="text-[#57534E] hover:text-[#AB1C24] flex items-center gap-1.5 text-sm font-medium">
          <ArrowLeft size={16} weight="bold" /> Volver
        </button>
        <div className="flex-1" />
        <Input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} className="w-[160px]" data-testid="report-date" />
        <Button
          onClick={() => handleSave(false)}
          disabled={saving || sending}
          variant="outline"
          className="gap-2"
          data-testid="report-save-btn"
        >
          <FloppyDisk size={16} weight="fill" /> {saving ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button
          onClick={() => {
            if (window.confirm(`¿Enviar este informe por email a ${data.student.email}?`)) handleSave(true);
          }}
          disabled={saving || sending}
          className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          data-testid="report-send-btn"
        >
          <PaperPlaneTilt size={16} weight="fill" /> {sending ? 'Enviando…' : 'Enviar por email'}
        </Button>
        <Button onClick={handlePrint} className="bg-[#AB1C24] hover:bg-[#8A151C] gap-2" data-testid="report-print-btn">
          <Printer size={16} weight="fill" /> PDF / Imprimir
        </Button>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mb-4 text-xs text-[#78716C] flex flex-wrap items-center gap-2 print:hidden" data-testid="report-history">
          <span className="font-semibold text-[#57534E]">Histórico:</span>
          {history.slice(0, 5).map(r => (
            <span key={r.report_id} className={`px-2 py-0.5 rounded-full border ${r.sent_at ? 'border-emerald-400 text-emerald-700 bg-emerald-50' : 'border-stone-300 text-stone-600 bg-white'}`}>
              {r.report_date} {r.sent_at ? '✉' : '·'} {r.sent_at ? 'enviado' : 'guardado'}
            </span>
          ))}
        </div>
      )}

      {lastSavedId && (
        <div className="mb-4 inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 print:hidden">
          <CheckCircle size={14} weight="fill" /> Informe guardado
        </div>
      )}

      {/* Printable report */}
      <article className="bg-white shadow-sm rounded-2xl print:rounded-none print:shadow-none p-8 sm:p-12 print:p-10 space-y-8" data-testid="report-page">
        <header className="flex items-start justify-between border-b border-[#E7E5E4] pb-5">
          <div>
            <Wordmark size="md" />
            <p className="text-xs text-[#78716C] mt-1">Informe de evolución · bi-semanal</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold text-[#292524]">{data.student.name}</p>
            <p className="text-[#78716C]">{data.student.email}</p>
            <p className="text-[#78716C]">Nivel {data.student.level}</p>
            <p className="text-[#A8A29E] text-xs mt-1">{new Date(reportDate).toLocaleDateString('es-ES', { dateStyle: 'long' })}</p>
          </div>
        </header>

        {/* Editable: intro */}
        <EditableSection
          label="Introducción"
          value={editable.intro}
          onChange={v => updateField('intro', v)}
          placeholder="Mensaje introductorio al alumno…"
          rows={3}
          testId="rep-intro"
        />

        {/* Numbers snapshot */}
        <section>
          <h3 className="font-heading text-lg font-bold text-[#292524] mb-3">Datos de estas 2 semanas</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Metric label="Ejercicios" value={data.summary.exercises_done} />
            <Metric label="Aciertos" value={`${data.summary.accuracy}%`} />
            <Metric label="Foro" value={data.summary.forum_posts} />
            <Metric label="Voz" value={data.summary.voice_notes} />
            <Metric label="Ayudas 🏆" value={data.summary.help_given || 0} />
          </div>
          {(data.summary.answers_posted || 0) > 0 && (
            <p className="text-xs text-[#78716C] mt-2">
              <strong>Intervenciones en español:</strong> {data.summary.answers_posted} respuesta{data.summary.answers_posted === 1 ? '' : 's'} publicada{data.summary.answers_posted === 1 ? '' : 's'} en el foro de Dudas ·{' '}
              {data.summary.help_given_total || 0} aceptada{(data.summary.help_given_total || 0) === 1 ? '' : 's'} en total.
            </p>
          )}
        </section>

        {/* Skill table */}
        {data.skills.length > 0 && (
          <section>
            <h3 className="font-heading text-lg font-bold text-[#292524] mb-3">Precisión por competencia</h3>
            <table className="w-full text-sm border border-[#E7E5E4] rounded-lg overflow-hidden">
              <thead className="bg-[#FAF9F6] text-[#57534E]">
                <tr>
                  <th className="text-left px-3 py-2">Competencia</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Aciertos</th>
                  <th className="text-right px-3 py-2">%</th>
                </tr>
              </thead>
              <tbody>
                {data.skills.map(s => (
                  <tr key={s.skill} className="border-t border-[#E7E5E4]">
                    <td className="px-3 py-2 capitalize">{s.skill.replaceAll('_', ' ')}</td>
                    <td className="px-3 py-2 text-right">{s.total}</td>
                    <td className="px-3 py-2 text-right">{s.correct}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${s.accuracy >= 75 ? 'text-green-700' : s.accuracy >= 50 ? 'text-amber-700' : 'text-red-700'}`}>
                      {s.accuracy}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Weak spots */}
        {data.weak_spots.length > 0 && (
          <section>
            <h3 className="font-heading text-lg font-bold text-[#292524] mb-3">Puntos a trabajar</h3>
            <ul className="space-y-2">
              {data.weak_spots.map((w, i) => (
                <li key={i} className="border border-[#E7E5E4] rounded-lg p-3">
                  <p className="text-sm font-medium">{w.question || '(sin enunciado)'}</p>
                  <p className="text-xs text-[#78716C] mt-1">
                    Correcto: <strong>{w.correct_answer}</strong> · fallado {w.misses}x · {w.level}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Editable: strengths */}
        <EditableSection
          label="Fortalezas"
          value={editable.strengths}
          onChange={v => updateField('strengths', v)}
          placeholder="Destaca 2–3 logros concretos del alumno…"
          rows={3}
          testId="rep-strengths"
        />

        {/* Editable: to improve */}
        <EditableSection
          label="Áreas por mejorar"
          value={editable.to_improve}
          onChange={v => updateField('to_improve', v)}
          placeholder="Identifica los puntos a reforzar con acciones concretas…"
          rows={3}
          testId="rep-improve"
        />

        {/* Editable: advice */}
        <EditableSection
          label="Consejo personalizado de Alberto"
          value={editable.advice}
          onChange={v => updateField('advice', v)}
          placeholder="Ej: Cada noche repasa 5 verbos irregulares en voz alta antes de dormir…"
          rows={4}
          testId="rep-advice"
          highlight
        />

        {/* Editable: closing */}
        <EditableSection
          label="Despedida"
          value={editable.closing}
          onChange={v => updateField('closing', v)}
          placeholder="Tu mensaje de cierre…"
          rows={2}
          testId="rep-closing"
        />

        <footer className="pt-4 border-t border-[#E7E5E4] text-center text-xs text-[#A8A29E]">
          Habla · academia de español · {data.teacher.name}
        </footer>
      </article>

      <style>{`
        @media print {
          body { background: white; }
          header, nav, .print\\:hidden { display: none !important; }
          @page { size: A4; margin: 1.2cm; }
        }
      `}</style>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div className="border border-[#E7E5E4] rounded-lg p-3 text-center">
    <p className="text-2xl font-heading font-bold text-[#AB1C24]">{value}</p>
    <p className="text-xs text-[#78716C] mt-0.5">{label}</p>
  </div>
);

const EditableSection = ({ label, value, onChange, placeholder, rows = 3, testId, highlight }) => (
  <section className={`rounded-lg ${highlight ? 'bg-[#FDF0ED] border border-[#AB1C24]/20 p-4' : ''}`}>
    <div className="flex items-center gap-1.5 mb-2">
      <PencilSimpleLine size={14} className="text-[#AB1C24] print:hidden" />
      <h3 className={`font-heading font-bold text-[#292524] ${highlight ? 'text-lg' : 'text-base'}`}>{label}</h3>
    </div>
    <Textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full resize-y print:border-none print:bg-transparent print:resize-none"
      data-testid={testId}
    />
  </section>
);
